import { ActionTree } from 'vuex';
import {
  AdmissionType,
  AdmissionState,
  EvaluationEnem,
  EvaluationSecondGraduation,
  EvaluationTransferGraduation,
  Institution,
} from './state';
import RootState from '@/store/root_state';
import { AdmissionType as StateAdmissionTypeKey } from '@/pipeline/stateMachine';
import { MicroserviceEnum, mutation, query } from '@/helpers/westeros';
import {
  allAdmissionTypes,
  AllAdmissionTypesQueryResult,
  allInstitutions,
  AllInstitutionsQueryResult,
  getAdmission,
  GetAdmissionQueryResult,
  getSettings,
  GetSettingsQueryResult,
} from '@/store/graphql/queries/westerosQueries';
import {
  upsertAdmission,
  UpsertAdmissionMutationResult,
  UpsertAdmissionMutationInput,
  uploadSecondGraduationDiploma,
  uploadTransferTranscript,
} from '@/store/graphql/mutations/westerosMutations';
import { normalizeAdmission } from '../../graphql/normalizer';
import pipeline from '@/pipeline';
import steps from '@/pipeline/steps';

export const AdmissionActions = {
  CHECK_ADMISSION_AVAILABILITIES: 'CHECK_ADMISSION_AVAILABILITIES',
};

const actions: ActionTree<AdmissionState, RootState> = {
  async PULL_CURRENT_ADMISSION({ commit }): Promise<void> {
    const { data } = await query<GetAdmissionQueryResult>(
      MicroserviceEnum.ADMISSIONS,
      getAdmission,
    );

    const normalizedAdmission = normalizeAdmission(data?.getAdmission);

    commit('SET_ADMISSION_ID', normalizedAdmission.admissionId);
    commit('SET_EVALUATION_STATUS', normalizedAdmission.evaluationStatus);
    commit('SET_USER', normalizedAdmission.user);
    commit('SET_COURSE', normalizedAdmission.course);
    commit('SET_CITY', normalizedAdmission.city);
    commit('SET_CAMPUS', normalizedAdmission.campus);
    commit('SET_ADMISSION_TYPE', normalizedAdmission.admissionType);
    commit('SET_ESSAY', normalizedAdmission.essay);
    commit('SET_ESSAY_THEME', normalizedAdmission.essayTheme);
    commit('SET_ENEM', normalizedAdmission.enem);
    commit('SET_SECOND_GRADUATION', normalizedAdmission.secondGraduation);
    commit('SET_TRANSFER_GRADUATION', normalizedAdmission.transfer);
  },
  async CREATE_NEW_ADMISSION({ dispatch }): Promise<void> {
    await dispatch('UPSERT_ADMISSION', {});
  },

  async UPSERT_ADMISSION({ commit }, payload: UpsertAdmissionMutationInput): Promise<void> {
    const { data } = await mutation<UpsertAdmissionMutationResult>(
      MicroserviceEnum.ADMISSIONS,
      upsertAdmission,
      { data: payload },
    );

    const normalizedAdmission = normalizeAdmission(data?.upsertAdmission);

    commit('SET_ADMISSION_ID', normalizedAdmission.admissionId);
    commit('SET_EVALUATION_STATUS', normalizedAdmission.evaluationStatus);
    commit('SET_USER', normalizedAdmission.user);
    commit('SET_COURSE', normalizedAdmission.course);
    commit('SET_CITY', normalizedAdmission.city);
    commit('SET_CAMPUS', normalizedAdmission.campus);
    commit('SET_ADMISSION_TYPE', normalizedAdmission.admissionType);
    commit('SET_ESSAY', normalizedAdmission.essay);
    commit('SET_ESSAY_THEME', normalizedAdmission.essayTheme);
    commit('SET_ENEM', normalizedAdmission.enem);
    commit('SET_SECOND_GRADUATION', normalizedAdmission.secondGraduation);
    commit('SET_TRANSFER_GRADUATION', normalizedAdmission.transfer);
  },

  async UPDATE_ADMISSION_TYPE(
    { dispatch },
    payload: { admissionType: AdmissionType },
  ): Promise<void> {
    const { admissionType } = payload;

    const params: UpsertAdmissionMutationInput = { admissionTypeId: admissionType.id };

    await dispatch('UPSERT_ADMISSION', params);
  },

  async PULL_AVAILABLE_ADMISSION_TYPES({ commit }): Promise<void> {
    const { data } = await query<AllAdmissionTypesQueryResult>(
      MicroserviceEnum.ADMISSIONS,
      allAdmissionTypes,
      undefined,
      true,
    );

    const admissionTypes: AdmissionType[] = data.allAdmissionTypes.map(
      ({ id, name, description, evaluationMethod }) => ({
        id,
        name,
        description,
        stateKey: evaluationMethod as StateAdmissionTypeKey,
      }),
    );

    commit('SET_AVAILABLE_ADMISSION_TYPES', admissionTypes);
  },

  async PULL_AVAILABLE_ENEM_YEARS({ commit }): Promise<void> {
    const { data } = await query<GetSettingsQueryResult>(
      MicroserviceEnum.ADMISSIONS,
      getSettings,
      { key: 'enemYears' },
      true,
    ).catch(() => ({ data: { getSettings: { value: [] } } }));

    const enemYears: string[] = data.getSettings.value as string[];

    commit('SET_AVAILABLE_ENEM_YEARS', enemYears);
  },

  async UPDATE_ENEM({ dispatch }, payload: { enem: EvaluationEnem }): Promise<void> {
    const { enem } = payload;

    const params: UpsertAdmissionMutationInput = {
      evaluationEnem: enem
        ? {
            inscription: enem.subscriptionNumber,
            year: enem.year ? Number(enem.year) : undefined,
            languagesGrade: enem.grades?.languages,
            humanScienceGrade: enem.grades?.humanScience,
            natureScienceGrade: enem.grades?.natureScience,
            mathGrade: enem.grades?.math,
            essayGrade: enem.grades?.essay,
            hasAcceptedToS: enem.hasAcceptedToS,
          }
        : null,
    };

    await dispatch('UPSERT_ADMISSION', params);
  },

  async PULL_EVALUATION_STATUS({ dispatch }): Promise<void> {
    await dispatch('PULL_CURRENT_ADMISSION');
  },

  async UPDATE_SECOND_GRADUATION(
    { dispatch },
    payload: { secondGraduation: EvaluationSecondGraduation },
  ): Promise<void> {
    const { secondGraduation } = payload;

    const params: UpsertAdmissionMutationInput = {
      evaluationSecondGraduation: secondGraduation
        ? {
            courseName: secondGraduation.courseName,
            year: secondGraduation.year,
            institution: secondGraduation.institution,
            hasAcceptedToS: secondGraduation.hasAcceptedToS,
          }
        : null,
    };

    await dispatch('UPSERT_ADMISSION', params);
  },

  async UPDATE_TRANSFER_GRADUATION(
    { dispatch },
    payload: { transferGraduation: EvaluationTransferGraduation },
  ): Promise<void> {
    const { transferGraduation } = payload;
    const params: UpsertAdmissionMutationInput = {
      evaluationTransfer: transferGraduation
        ? {
            courseName: transferGraduation.courseName,
            period: transferGraduation.periodYear,
            institution: transferGraduation.institution,
            hasAcceptedToS: transferGraduation.hasAcceptedToS,
          }
        : null,
    };

    await dispatch('UPSERT_ADMISSION', params);
  },

  async PULL_AVAILABLE_INSTITUTIONS({ commit }, payload: { name?: string } = {}): Promise<void> {
    const { name = '' } = payload;

    const { data } = await query<AllInstitutionsQueryResult>(
      MicroserviceEnum.ADMISSIONS,
      allInstitutions,
      { nameContains: name, take: 10 },
      true,
    ).catch(() => ({ data: { allInstitutions: [] } }));

    const availableInstitutions: Institution[] = data.allInstitutions;

    commit('SET_AVAILABLE_INSTITUTIONS', availableInstitutions);
  },

  async UPLOAD_SECOND_GRADUATION_DOCUMENT({ commit }, payload): Promise<void> {
    const { admissionId, fileName, fileDataUrl, side } = payload;

    await mutation<Boolean>(MicroserviceEnum.ADMISSIONS, uploadSecondGraduationDiploma, {
      admissionId,
      fileName,
      fileDataUrl,
      side,
    });

    const documentProp = side === 'back' ? 'documentBack' : 'document';

    const secondGraduation: EvaluationSecondGraduation = {
      [documentProp]: {
        name: fileName,
        dataUrl: fileDataUrl,
      },
    };

    commit('SET_SECOND_GRADUATION', secondGraduation);
  },

  async DELETE_SECOND_GRADUATION_DOCUMENT({ commit }, payload): Promise<void> {
    // Deleting will stay like this or now. The user won't be able to
    // delete as uploading the docs is the last step of the admission.

    const { side } = payload;

    const documentProp = side === 'back' ? 'documentBack' : 'document';
    const secondGraduation: EvaluationSecondGraduation = {
      [documentProp]: {},
    };

    commit('SET_SECOND_GRADUATION', secondGraduation);
  },

  async UPLOAD_TRANSFER_GRADUATION_DOCUMENT({ commit }, payload): Promise<void> {
    const { admissionId, fileName, fileDataUrl } = payload;

    await mutation<Boolean>(MicroserviceEnum.ADMISSIONS, uploadTransferTranscript, {
      admissionId,
      fileName,
      fileDataUrl,
    });

    const transferGraduation: EvaluationTransferGraduation = {
      document: {
        name: fileName,
        dataUrl: fileDataUrl,
      },
    };

    commit('SET_TRANSFER_GRADUATION', transferGraduation);
  },

  async DELETE_TRANSFER_GRADUATION_DOCUMENT({ commit }): Promise<void> {
    // Deleting will stay like this or now. The user won't be able to
    // delete as uploading the docs is the last step of the admission.
    const transferGraduation: EvaluationTransferGraduation = {
      document: {},
    };

    commit('SET_TRANSFER_GRADUATION', transferGraduation);
  },

  async CHECK_ADMISSION_AVAILABILITIES({ dispatch, getters }): Promise<void> {
    const { selectedCourse, selectedCampus, evaluationStatus } = getters;

    const isAdmissionSuccess = evaluationStatus === 'success';

    if (isAdmissionSuccess) {
      return;
    }

    const needResetCourse = selectedCourse && !selectedCourse.isAvailable;
    const needResetCampus = selectedCampus && !selectedCampus.isAvailable;

    if (needResetCourse) {
      await dispatch('UPDATE_COURSE', { course: { id: null } });
    }

    if (needResetCampus) {
      await dispatch('UPDATE_CAMPUS', { campus: { id: null } });
    }

    if (needResetCourse) {
      pipeline.goTo(steps.courseSelection);
      pipeline.resetHistory();
    } else if (needResetCampus) {
      pipeline.goTo(steps.citySelection);
      pipeline.resetHistory();
    }
  },
};

export default actions;
